import { useEffect, useMemo, useState } from 'react';
import {
  generateStateKey,
  readGeneratedReport,
  subscribeGeneratedReports,
} from '../reportTabData';
import type { GenerateState } from '../generateReport';
import type { GeneratedTabPaneProps } from './GeneratedReportTabs';

/**
 * Phase 5f (builder A) — submittal_package, usage_statistics and
 * integrated_project_ledger, ported 1:1 from the legacy tabs. Legacy reality
 * per tab (all verified from source):
 *
 * submittal_package (view/submittal.package.js, 611 lines — almost all dead):
 *  - LIVE generate: preGenerateReport (:395-403) `delete data.ahu_id`, fetches
 *    the project's fans and draws the curve canvases (ALL in the kit's
 *    fanCurveImages provider), then generateReport (:434-472) POSTs
 *    'submittal_package' — the $.ajax SUCCESS render()s (:66-73) the SAME
 *    static shell every time (submittal.package.tmpl:1-18): header with empty
 *    .time-stamp and .name_for_print spans + generation-in-progress preloader
 *    + empty #submittal_package_structure. The template gets
 *    `data: self.data[0]` (:71) — always undefined (the cache is keyed by
 *    string states) and unused by the template — noise, not ported.
 *  - LIVE socket wait (:452-470, UNCOMMENTED): Promise.all([socket, req]) →
 *    task_id match → `self.data[state] = data` under the 2-PART key
 *    `current_report + project_id` (:437) — its only live effect (the viewPDF
 *    call is commented out, :463-467). That cache write arms the reopen:
 *  - LIVE reopen: openGeneratedReport (:375-385) — cache hit → renderReport +
 *    generated=true. renderReport's body is 100% commented out (:76-102), a
 *    NO-OP — so a reopen just removes the empty board (action.js:60-64) and
 *    re-shows the pane's STALE shell, whose generation-in-progress container
 *    still carries the 4.5s 'opacity-0' (action.js:124-128 — nothing ever
 *    removes it). Reproduced by REOPENABLE_TABS + the page's reopen mount
 *    (mode='reopen', faded=true, rendered=true) + the 2-part cache key
 *    (reportTabData PROJECT_KEYED_REPORTS) + the kit's socket→cache write.
 *  - Dead machinery NOT ported:
 *     · renderReport (:76-102) fully commented; report.project.tmpl (compiled
 *       :51, used only at commented :84) and equipment_list.tmpl (compiled
 *       :50, never used) are dead loads; setTimeStamp (:603-605) only ran
 *       from the commented body → .time-stamp stays EMPTY forever.
 *     · print (:103-129) — trigger 'print:report:submittal_fan_data' only
 *       fires from the header print harness whose buttons no longer render
 *       (main_header.tmpl renders only #generate_report, 5a/5c-verified);
 *       it also reads `self.hhh` which NO code ever assigns → would throw.
 *     · export_csv (:512-569) — 'export_csv:report:submittal_package' hangs
 *       off the dead header #export_csv chain (main_header.js:62).
 *     · viewPDF (:419-433) only called from commented code (:463-467);
 *       validateOutputs (:492-510) unreferenced (and reads an undefined
 *       `number`); `$('#export_pdf')` hidden-toggles (:68, :378) are no-ops
 *       — the live header renders no #export_pdf.
 *     · initialize constructs ahu/cmsProduct/configuration/weatherRating/
 *       submittalFormat collections (:52-58) but NEVER fetches any — Backbone
 *       construction is network-free (collection sources set this.url only),
 *       so the `/api/v1.0/user_settings/submittal/format/` collection makes
 *       NO request and no format UI exists in the live tab. Only
 *       FansCollection is fetched, per generate (:398-399 — in the kit's
 *       curve provider). Tunnels/TunnelIndexes/user (:11-15) are loaded but
 *       never instantiated/used live.
 *     · report.name.tmpl.mapping.js is a dead file: app.js:95 maps
 *       'report.name.view.mapping' to 'tabs/submittal_package/
 *       report.name.mapping' (a path that does not exist) and nothing
 *       requires that module id anyway; model/user.defined.field.js only
 *       feeds view/user.defined.field.js, reachable only through that dead
 *       mapping; model/all.js is empty.
 *
 * usage_statistics (view/usage_statistics.js, 243 lines):
 *  - LIVE generate: generateReport (:51-87) POSTs 'usage_statistics' with the
 *    whole dispatch state + the real mfg_id (:56 — kit dispatch,
 *    generateReport.ts) — SUCCESS → render() (:62-64) the static shell
 *    (usage_statistics.tmpl:1-18). The console.log (:54) is debug noise.
 *    The generate socket block is fully commented out (:71-86) — nothing
 *    ever writes `this.data[state]`.
 *  - LIVE-but-inert reopen: openGeneratedReport (:42-49) IS registered (:29)
 *    but its cache check (keyed by current_report alone, :43) can never hit
 *    (no live writer) → generated stays false → the empty board re-activates:
 *    the page default for tabs absent from REOPENABLE_TABS.
 *  - Dead machinery NOT ported: export_csv (:191-235) — dead header chain,
 *    and it reads `self.data[self.current_state].data.generate_id` which
 *    would throw (cache never written); export_pdf (:106-145) only called
 *    from the commented socket block; viewPDF (:88-104) begins with a bare
 *    `return;`; validate/generetionTimeStamp/setTimeStamp unreferenced from
 *    live paths; model/usage_statistics.js is required by no view.
 *
 * integrated_project_ledger (view/integrated_project_ledger.js, 93 lines):
 *  - Renders its static shell in initialize (:28 → render :31-37, which also
 *    clears the .report-tabs-preloader) — the filter_data pattern →
 *    IMMEDIATE_RENDER_TABS entry; `rendered` is ignored here.
 *  - LIVE generate: BOTH 'generate:report:…' and 'export_csv:report:…' point
 *    at export_csv (:26-27; only the generate trigger still fires). It adds
 *    'active' to the tab's .integrated-project-ledger-preloader (:56 — the
 *    template ships it INACTIVE, tmpl:12), POSTs
 *    'csv_integrated_project_ledger/' {mfg_id} and, on the socket message
 *    matching the task_id, strips 'active' BEFORE the url check (:75-77) and
 *    click-downloads the ZIP (:78-84). POST + socket wait + download are the
 *    kit's dispatch (generateReport.ts); the kit surfaces the delivery
 *    moment through a reportTabData cache write and the pane subscribes to
 *    strip 'active' (useLedgerDelivered below).
 *  - NOT reopenable: no 'open:generated_report:…' listener at all (:21-29) →
 *    reportIsGenerated leaves generated=false → empty board (page default).
 *  - Dead machinery NOT ported: getDashboardState (:45-51) is never called;
 *    model/integrated_project_ledger.js is required by no view. The
 *    structure div's static explainer paragraph (tmpl:15-20) is LIVE
 *    template content and ported verbatim — including its reference to an
 *    "Export CSV" toolbar button that no longer renders.
 */

// ---- Submittal Package ----

/** submittal.package.tmpl:1-18 — group "Engineering " (trailing space),
 *  tooltip ": ", ADJACENT report-name (no leading space), empty .time-stamp
 *  AND .name_for_print spans (whitespace-separated, tmpl:3-5). Shell shows on
 *  POST success (render :66-73) and on reopen — where the stale container
 *  keeps its 'opacity-0' (the page passes faded=true in reopen mode). */
export const SubmittalPackageTab = ({ report, faded, rendered, mode }: GeneratedTabPaneProps) => {
  const showShell = mode === 'reopen' || rendered;
  return (
    <div id={report.value} className="col-xs-12 active" style={{ display: 'block' }}>
      {showShell && (
        <>
          <div className="container-header clearfix">
            <h3 className="left">
              {/* sectionName = the .current_report menu button's text, i.e.
                  the report display name (submittal.package.js:69). */}
              <span className="group-name" tooltip={`: ${report.name}`}>
                Engineering{' '}
              </span>
              <span className="report-name">{report.name}</span>{' '}
              {/* Both spans stay empty: setTimeStamp (:603-605) and the
                  .name_for_print writer only exist in dead code. */}
              <span className="time-stamp" /> <span className="name_for_print" />
            </h3>
          </div>
          <div className="report-content col-xs-12">
            <div className="content">
              <div className="content-item full-height" style={{ overflow: 'auto' }}>
                <div
                  className={`preloader-container generation-in-progress${
                    faded ? ' opacity-0' : ''
                  }`}
                >
                  <div className="preloader active">Report generation in progress...</div>
                </div>
                <div id="submittal_package_structure" className="row full-height" />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// ---- Usage Statistics ----

/** usage_statistics.tmpl:1-18 — container-header carries col-xs-12 and nests
 *  another col-xs-12 div (the bill_of_materials shape), group "Purchasing"
 *  [sic — the template's literal, not the menu group], tooltip " : ",
 *  report-name with a leading space, NO time-stamp span. Shell shows on POST
 *  success only (render :62-64). */
export const UsageStatisticsTab = ({ report, faded, rendered }: GeneratedTabPaneProps) => (
  <div id={report.value} className="col-xs-12 active" style={{ display: 'block' }}>
    {rendered && (
      <>
        <div className="container-header clearfix col-xs-12">
          <div className="col-xs-12">
            <h3 className="left">
              <span className="group-name" tooltip={` : ${report.name}`}>
                Purchasing
              </span>
              <span className="report-name"> {report.name}</span>
            </h3>
          </div>
        </div>
        <div className="report-content col-xs-12">
          <div className="content">
            <div className="content-item full-height" style={{ overflow: 'auto' }}>
              <div
                className={`preloader-container generation-in-progress${
                  faded ? ' opacity-0' : ''
                }`}
              >
                <div className="preloader active">Report generation in progress...</div>
              </div>
              <div id="usage_statistics_structure" className="row full-height" />
            </div>
          </div>
        </div>
      </>
    )}
  </div>
);

// ---- Coil Analytics (dev-sync I) ----

/** tabs/coil_analytics/tmpl/coil_analytics.tmpl:1-18 on merged dev — byte-level
 *  the usage_statistics shell with group "Management" and the
 *  #coil_analytics_structure div. Shell shows on POST success only (the view's
 *  generateReport success → render(), coil_analytics.js:136-138).
 *  openGeneratedReport IS registered (:104) but its `this.data[state]` cache
 *  has no live writer (the generate socket block is fully commented out,
 *  :146-161) → never reopenable, exactly the usage_statistics pattern.
 *  Dead machinery NOT ported (same inventory as usage_statistics): export_csv
 *  (:266-310) hangs off the dead header #export_csv chain AND posts to
 *  'csv_coil_analytics/' — a route that does not exist in report/urls.py;
 *  export_pdf (:181-220, → nonexistent 'pdf_coil_analytics') is only called
 *  from the commented socket block; viewPDF (:163-179) begins with a bare
 *  `return;`. */
export const CoilAnalyticsTab = ({ report, faded, rendered }: GeneratedTabPaneProps) => (
  <div id={report.value} className="col-xs-12 active" style={{ display: 'block' }}>
    {rendered && (
      <>
        <div className="container-header clearfix col-xs-12">
          <div className="col-xs-12">
            <h3 className="left">
              <span className="group-name" tooltip={` : ${report.name}`}>
                Management
              </span>
              <span className="report-name"> {report.name}</span>
            </h3>
          </div>
        </div>
        <div className="report-content col-xs-12">
          <div className="content">
            <div className="content-item full-height" style={{ overflow: 'auto' }}>
              <div
                className={`preloader-container generation-in-progress${
                  faded ? ' opacity-0' : ''
                }`}
              >
                <div className="preloader active">Report generation in progress...</div>
              </div>
              <div id="coil_analytics_structure" className="row full-height" />
            </div>
          </div>
        </div>
      </>
    )}
  </div>
);

// ---- Integrated Project Ledger ----

/** The run's delivery signal: the kit's ledger dispatch writes the socket
 *  message to the reportTabData cache under the dispatch state's key the
 *  moment it arrives (mirroring the legacy preloader strip at
 *  integrated_project_ledger.js:75, which runs BEFORE the url check). Only a
 *  write NEWER than the dispatch-time baseline counts — a re-generate over a
 *  cached prior run re-baselines (`states` in the deps), the same pattern as
 *  the 5e useOrderEntry. */
function useLedgerDelivered(states?: GenerateState[]): boolean {
  const key = useMemo(() => {
    const last = states && states.length ? states[states.length - 1] : null;
    return last ? generateStateKey(last) : null;
  }, [states]);
  const [delivered, setDelivered] = useState(false);
  useEffect(() => {
    if (!key) return undefined;
    const baseline = readGeneratedReport(key);
    setDelivered(false);
    return subscribeGeneratedReports(() => {
      if (readGeneratedReport(key) !== baseline) setDelivered(true);
    });
  }, [key, states]);
  return delivered;
}

/** integrated_project_ledger.tmpl:1-25 — the usage_statistics header shape
 *  with group "Management"; the preloader ships INACTIVE (tmpl:12) and is
 *  activated by export_csv at the generate trigger (:56) — i.e. from the
 *  moment this pane mounts — then stripped on socket delivery (:75). Renders
 *  at mount (initialize → render, :28), so `rendered` is ignored like
 *  FilterDataTab. */
export const IntegratedProjectLedgerTab = ({ report, faded, states }: GeneratedTabPaneProps) => {
  const delivered = useLedgerDelivered(states);
  return (
    <div id={report.value} className="col-xs-12 active" style={{ display: 'block' }}>
      <div className="container-header clearfix col-xs-12">
        <div className="col-xs-12">
          <h3 className="left">
            <span className="group-name" tooltip={` : ${report.name}`}>
              Management
            </span>
            <span className="report-name"> {report.name}</span>
          </h3>
        </div>
      </div>
      <div className="report-content col-xs-12">
        <div className="content">
          <div className="content-item full-height" style={{ overflow: 'auto' }}>
            <div
              className={`preloader-container generation-in-progress${
                faded ? ' opacity-0' : ''
              }`}
            >
              <div
                className={`preloader integrated-project-ledger-preloader${
                  delivered ? '' : ' active'
                }`}
              >
                Report generation in progress...
              </div>
            </div>
            <div id="integrated_project_ledger_structure" className="row full-height">
              {/* tmpl:15-20 verbatim (whitespace collapses to single spaces). */}
              <p className="col-xs-12" style={{ padding: '24px' }}>
                The Integrated Project Ledger is a wide CSV export covering every project, its
                key metadata, and the scheduled and actual start/end dates of every workflow
                task. The export honors the dashboard&#39;s{' '}
                <strong>All / Active / Archived</strong> filter. Click <em>Export CSV</em> in
                the toolbar above to download.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
